import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Zap, BatteryCharging, Smartphone, Laptop, Watch, Cpu } from "lucide-react";
import { Reveal } from "./Reveal";
import { Eyebrow } from "./bits";

const DEVICES = [
  { id: "d1", name: "MacBook Pro", serial: "#12A6AA", icon: Laptop, battery: 88, rate: 0.9, max: 68, state: "Surface · fast" },
  { id: "d2", name: "iPhone 16", serial: "#8BF03C", icon: Smartphone, battery: 74, rate: 0.4, max: 30, state: "Ambient · top-up" },
  { id: "d3", name: "Watch Ultra", serial: "#4D19EE", icon: Watch, battery: 96, rate: 0.15, max: 9, state: "Ambient · trickle" },
  { id: "d4", name: "Room sensor", serial: "#A0C7B2", icon: Cpu, battery: 100, rate: 0.05, max: 3, state: "Maintained" },
];

export default function AppPreview() {
  const [wh, setWh] = useState({ d1: 41.6, d2: 12.3, d3: 3.1, d4: 0.8 });
  const ref = useRef(null);
  const inView = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => (inView.current = e.isIntersecting), { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    const iv = setInterval(() => {
      if (!inView.current) return;
      setWh((prev) => {
        const next = { ...prev };
        DEVICES.forEach((d) => { next[d.id] = +Math.min(d.max, prev[d.id] + d.rate).toFixed(1); });
        return next;
      });
    }, 900);
    return () => { clearInterval(iv); obs.disconnect(); };
  }, []);

  const total = Object.values(wh).reduce((a, b) => a + b, 0).toFixed(1);

  return (
    <section ref={ref} className="relative overflow-hidden bg-navy px-5 py-24 md:px-8 md:py-32 grain">
      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>The app</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl">
            Every watt-hour, on every device — <span className="text-revo-green">metered to you.</span>
          </h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-revo-ice">
            No wall charger ever told you what it delivered. ChargeRevo does. Watch each enrolled device sip power in real time — fast on surfaces, gentle in the room — all on one account, all billed by the watt-hour.
          </p>
          <div className="mt-8 flex gap-8">
            <div>
              <p className="font-display text-3xl font-black text-revo-green">{DEVICES.length}</p>
              <p className="text-sm text-revo-ice/60">devices enrolled</p>
            </div>
            <div>
              <p className="font-display text-3xl font-black text-white" data-testid="app-total-wh">{total}</p>
              <p className="text-sm text-revo-ice/60">watt-hours today</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="flex justify-center">
            <motion.div
              initial={{ rotateY: -8, rotateX: 4 }}
              whileHover={{ rotateY: 0, rotateX: 0 }}
              transition={{ type: "spring", stiffness: 120, damping: 14 }}
              className="relative w-[300px] rounded-[2.6rem] border-[6px] border-[#1a2c4d] bg-navy-deep p-4 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.7)]"
              data-testid="app-phone-mockup"
              style={{ perspective: 1000 }}
            >
              <div className="absolute left-1/2 top-3 h-5 w-24 -translate-x-1/2 rounded-full bg-[#1a2c4d]" />
              <div className="mt-6 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-revo-green"><Zap className="h-3.5 w-3.5 text-navy" fill="currentColor" /></span>
                  <span className="font-display text-sm font-extrabold text-white">ChargeRevo</span>
                </div>
                <span className="flex items-center gap-1 rounded-full bg-revo-green/15 px-2 py-1 text-[10px] font-bold text-revo-green">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-revo-green" /> LIVE
                </span>
              </div>

              <p className="mt-4 px-1 text-[11px] uppercase tracking-wider text-revo-ice/50">Your devices</p>
              <div className="mt-2 space-y-2">
                {DEVICES.map((d) => (
                  <div key={d.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid={`app-device-${d.id}`}>
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-revo-green"><d.icon className="h-4 w-4" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-white">{d.name}</p>
                        <p className="text-[10px] text-revo-ice/50">{d.serial} · {d.state}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display text-sm font-extrabold text-revo-green">{wh[d.id]}</p>
                        <p className="text-[9px] text-revo-ice/40">Wh</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                        <motion.div className="h-full rounded-full bg-gradient-to-r from-revo-green/70 to-revo-green" animate={{ width: `${d.battery}%` }} transition={{ duration: 0.6 }} />
                      </div>
                      <span className="flex items-center gap-0.5 text-[10px] font-semibold text-white">
                        <BatteryCharging className="h-3 w-3 text-revo-green" />{d.battery}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
